/**
 * FadeIn - Reusable fade-in animation wrapper
 *
 * Supports multiple animation directions and scroll-triggered reveals.
 */

import { motion, type Variants } from 'framer-motion'
import { type ReactNode } from 'react'
import { DURATION, EASE, SCROLL_REVEAL } from './motionVariants'
import { useReducedMotion } from './useReducedMotion'

type Direction = 'up' | 'down' | 'left' | 'right' | 'none'

interface FadeInProps {
  children: ReactNode
  direction?: Direction
  delay?: number
  duration?: number
  /** Use scroll-triggered animation via useInView */
  useScroll?: boolean
  /** Custom className */
  className?: string
  /** Custom styles */
  style?: React.CSSProperties
  /** Element to render as */
  as?: 'div' | 'section' | 'article' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4'
}

const getDirectionOffset = (direction: Direction): { x: number; y: number } => {
  switch (direction) {
    case 'up': return { x: 0, y: 20 }
    case 'down': return { x: 0, y: -20 }
    case 'left': return { x: 20, y: 0 }
    case 'right': return { x: -20, y: 0 }
    case 'none': return { x: 0, y: 0 }
  }
}

export function FadeIn({
  children,
  direction = 'up',
  delay = 0,
  duration = DURATION.slow,
  useScroll = false,
  className,
  style,
  as = 'div',
}: FadeInProps) {
  const prefersReducedMotion = useReducedMotion()
  const offset = getDirectionOffset(direction)

  // Reduced motion: render fully visible with no hidden state or scroll gating.
  if (prefersReducedMotion) {
    const Tag = as
    return <Tag className={className} style={style}>{children}</Tag>
  }

  const variants: Variants = {
    hidden: {
      opacity: 0,
      x: offset.x,
      y: offset.y,
    },
    visible: {
      opacity: 1,
      x: 0,
      y: 0,
      transition: {
        duration,
        ease: EASE.editorial,
        delay,
      },
    },
  }

  const MotionComponent = motion[as]

  return (
    <MotionComponent
      initial="hidden"
      animate={useScroll ? undefined : 'visible'}
      whileInView={useScroll ? 'visible' : undefined}
      viewport={useScroll ? { once: SCROLL_REVEAL.once, margin: SCROLL_REVEAL.margin } : undefined}
      variants={variants}
      className={className}
      style={style}
    >
      {children}
    </MotionComponent>
  )
}

/**
 * MaskReveal - Editorial "slide up within container" effect
 *
 * Creates the Linear/Vercel style reveal where content slides up
 * within an overflow:hidden container.
 */
interface MaskRevealProps {
  children: ReactNode
  delay?: number
  duration?: number
  className?: string
  style?: React.CSSProperties
}

export function MaskReveal({
  children,
  delay = 0,
  duration = DURATION.entrance,
  className,
  style,
}: MaskRevealProps) {
  const prefersReducedMotion = useReducedMotion()

  if (prefersReducedMotion) {
    return <div className={className} style={style}>{children}</div>
  }

  const containerVariants: Variants = {
    hidden: {},
    visible: {},
  }

  const childVariants: Variants = {
    hidden: {
      y: '100%',
      opacity: 0,
    },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration,
        ease: EASE.editorial,
        delay,
      },
    },
  }

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={containerVariants}
      className={className}
      style={{
        overflow: 'hidden',
        ...style,
      }}
    >
      <motion.div variants={childVariants}>
        {children}
      </motion.div>
    </motion.div>
  )
}
